

















































































































  import { defineComponent, computed } from 'vue'
  import { Stocks, Products } from '@one/types'
  import useCurrentInstance from '~/utils/useCurrentInstance'
  import useUtils from '~/utils/useUtils'
  import OneProductStocksLabel from '~/components/organisms/product/OneProductStocksLabel.vue'
  import StockVisibilityMode = Stocks.StockVisibilityMode
  import { useCurrentProduct } from '~/composables/useCurrentProduct'

  enum WarehouseType {
    CENTRAL = 'CENTRAL',
    LOCAL = 'LOCAL',
  }

  export default defineComponent({
    name: 'OneProductStocks',
    components: {
      OneProductStocksLabel,
      OneProductStocksPopupDialog: () => import('~/components/organisms/product/OneProductStocksPopupDialog.vue'),
    },
    inject: {
      injectedCurrentProduct: { from: 'currentProduct', default: null },
    },
    setup() {
      const vm = useCurrentInstance()
      const { isTileCalculator } = useUtils(vm)

      const productBridge = computed(() => (vm as any)?.injectedCurrentProduct || null)

      const { product } = useCurrentProduct(productBridge)

      const getWarehouse = computed(() => vm.$store.getters['stocks/getWarehouse'])
      const getProductWarehouse = computed(() => vm.$store.getters['stocks/getProductWarehouse'])
      const getProductOrderQuantityInWarehouse = computed(() => vm.$store.getters['stocks/getProductOrderQuantityInWarehouse'])
      const getDefaultWarehouseId = computed(() => vm.$store.getters['stocks/getDefaultWarehouseId'])
      const getDefaultWarehouse = computed(() => vm.$store.getters['stocks/getDefaultWarehouse'])
      const getProductLabelsStore = computed(() => vm.$store.getters['products/getProductLabels'])
      const getProductOrderUnit = computed(() => vm.$store.getters['products/getProductOrderUnit'])
      const getProductData = computed(() => vm.$store.getters['products/getProduct'])
      const warehouseSelectionBlocked = computed(() => vm.$store.getters['account/warehouseSelectionBlocked'])
      const showStockSummary = computed(() => vm.$store.getters['cms/configuration']?.stockSummary)
      const isShowroomWarehouseEnabled = computed(() => vm.$store.getters['cms/configuration']?.showroomWarehouse)
      const centralWarehouseIds = computed<string[]>(() => vm.$store.getters['stocks/getCentralWarehouse'] || [])
      const localWarehouseIds = computed<string[]>(() => vm.$store.getters['stocks/getLocalWarehouses'] || [])

      const warehouse = (id: string) => getProductWarehouse.value(product.value?.id, id)
      const getLabel = (id: string) => warehouse(id)?.warehouseLabel ?? '-'
      const getAvailability = (id: string) => warehouse(id)?.available
      const getShippingTime = (id: string) => getWarehouse.value(id)?.shippingTime

      const stocks = computed(() => {
        return product.value?.stocks || null
      })

      const areStocksAvailable = computed(() => stocks.value !== null)

      const isProductBlocked = computed((): boolean => {
        return getProductLabelsStore.value('Block', product.value?.id)
      })

      const currentWarehouse = computed(() => {
        return getWarehouse.value(getDefaultWarehouseId.value)
      })

      const parentWarehouse = computed(() => {
        return currentWarehouse.value && getWarehouse.value(currentWarehouse.value.parent)
      })

      const isCentralWarehouse = computed((): boolean => {
        return currentWarehouse.value?.type === WarehouseType.CENTRAL
      })

      const orderQuantityInParentWarehouse = computed((): number => {
        if (!product.value) return 0

        return isTileCalculator(product.value as unknown as Products.Product)
          ? stocks.value?.warehouses.byId[getDefaultWarehouse.value?.parent]?.quantity ?? 0
          : getProductOrderQuantityInWarehouse.value(product.value.id, parentWarehouse.value?.id)
      })

      const availabilityInParentWarehouse = computed((): boolean => {
        if (!product.value) return false

        if (isTileCalculator(product.value as unknown as Products.Product)) {
          const parentId = getDefaultWarehouse.value?.parent
          return parentId ? !!stocks.value?.warehouses.byId[parentId]?.available : false
        }

        return parentWarehouse.value?.id
          ? !!stocks.value?.warehouses.byId[parentWarehouse.value.id]?.available
          : false
      })

      const stockVisibilityModeHidden = computed((): boolean => {
        return stocks.value?.stockVisibilityMode === StockVisibilityMode.HIDDEN // lub Orderpath.App.Shared.StockVisibilityMode.HIDDEN
      })

      const orderQuantityInSelectedWarehouse = computed((): number => {
        if (!product.value) return 0

        return isTileCalculator(product.value as unknown as Products.Product)
          ? stocks.value?.warehouses.byId[getDefaultWarehouseId.value]?.quantity ?? 0
          : getProductOrderQuantityInWarehouse.value(product.value.id, getDefaultWarehouseId.value)
      })

      const availabilityInSelectedWarehouse = computed((): boolean => {
        if (!product.value) return false
        return !!stocks.value?.warehouses.byId[getDefaultWarehouseId.value]?.available
      })

      const unit = computed(() => {
        return isTileCalculator(product.value as unknown as Products.Product)
          ? getProductData.value(product.value?.id)?.measurementUnits?.contentUnitDescription
          : getProductOrderUnit.value(product.value?.id)
      })

      const labels = computed(() => {
        return {
          parentWarehouse: parentWarehouse.value ? getLabel(parentWarehouse.value.id) : '-',
          defaultWarehouse: getLabel(getDefaultWarehouseId.value),
        }
      })

      const shippingTime = computed(() => {
        return {
          parentWarehouse: parentWarehouse.value ? getShippingTime(parentWarehouse.value.id) : '-',
          defaultWarehouse: getShippingTime(getDefaultWarehouseId.value),
        }
      })

      const displayProductTimeInfo = computed(() => {
        if (isProductBlocked.value && orderQuantityInParentWarehouse.value === 0) {
          return ''
        } else if (!isProductBlocked.value && orderQuantityInParentWarehouse.value === 0) {
          return product.value?.shippingTime
        } else {
          return shippingTime.value.parentWarehouse
            ? `${shippingTime.value.parentWarehouse} ${vm.$t('product_page.hour')}`
            : ''
        }
      })

      const defaultWarehouseDeliveryInfo = computed(() => {
        if (isProductBlocked.value && orderQuantityInSelectedWarehouse.value === 0) {
          return ''
        } else if (!isProductBlocked.value && orderQuantityInSelectedWarehouse.value === 0) {
          return product.value?.shippingTime
        } else {
          return shippingTime.value.defaultWarehouse
            ? `${shippingTime.value.defaultWarehouse} ${vm.$t('product_page.hour')}`
            : ''
        }
      })

      const warehousesList = computed(() => {
        if (!stocks.value) return []

        return stocks.value.warehouses.allIds
          .map((stockId: string) => ({
            id: stockId,
            unity: stocks.value?.unit,
            quantity: getProductOrderQuantityInWarehouse.value(product.value?.id, stockId),
            label: getLabel(stockId),
            shippingTime: getShippingTime(stockId),
            type: getWarehouse.value(stockId)?.type,
            available: getAvailability(stockId),
            stockVisibilityModeHidden: stockVisibilityModeHidden.value,
          }))
          .filter(
            (item: any) =>
              item.id !== getDefaultWarehouseId.value && item.id !== parentWarehouse.value?.id
          )
          .sort((a: any, b: any): number => {
            if (a.label < b.label) return -1
            if (a.label > b.label) return 1
            return 0
          })
      })

      const mapWarehousesByIds = (ids: string[]) => {
        if (!stocks.value) return []

        return ids
          .filter((stockId: string) => !!stocks.value?.warehouses.byId[stockId])
          .map((stockId: string) => ({
            id: stockId,
            unity: stocks.value?.unit,
            quantity: getProductOrderQuantityInWarehouse.value(product.value?.id, stockId),
            label: getLabel(stockId),
            shippingTime: getShippingTime(stockId),
            type: getWarehouse.value(stockId)?.type,
            available: getAvailability(stockId),
            stockVisibilityModeHidden: stockVisibilityModeHidden.value,
          }))
          .sort((a: any, b: any): number => {
            if (a.label < b.label) return -1
            if (a.label > b.label) return 1
            return 0
          })
      }

      const centralWarehouses = computed(() => mapWarehousesByIds(centralWarehouseIds.value))
      const localWarehouses = computed(() => mapWarehousesByIds(localWarehouseIds.value))

      const sumQuantity = (list: any[]): number => {
        return list.reduce((sum: number, item: any) => sum + (item.available ? (item.quantity ?? 0) : 0), 0)
      }

      const warehouseGroups = computed(() => [
        {
          key: 'central',
          title: vm.$t('product_page.shipping_warehouses'),
          warehouses: centralWarehouses.value,
          quantity: Math.floor(sumQuantity(centralWarehouses.value)),
          available: centralWarehouses.value.some((item: any) => item.available),
        },
        {
          key: 'local',
          title: vm.$t('product_page.pickup_warehouses'),
          warehouses: localWarehouses.value,
          quantity: Math.floor(sumQuantity(localWarehouses.value)),
          available: localWarehouses.value.some((item: any) => item.available),
        },
      ])

      const calculateSummaryStock = (): number => {
        const defaultQty = availabilityInSelectedWarehouse.value
          ? (stocks.value?.orderQuantityInSelectedWarehouse ?? 0)
          : 0

        const firstQty = availabilityInParentWarehouse.value
          ? (stocks.value?.orderQuantityInParentWarehouse ?? 0)
          : 0

        return Math.floor(stocks.value!.overallQuantity - (defaultQty + firstQty))
      }

      return {
        $utils: vm.$utils,
        product,
        areStocksAvailable,
        parentWarehouse,
        stockVisibilityModeHidden,
        availabilityInParentWarehouse,
        orderQuantityInParentWarehouse,
        unit,
        labels,
        shippingTime,
        displayProductTimeInfo,
        availabilityInSelectedWarehouse,
        orderQuantityInSelectedWarehouse,
        isCentralWarehouse,
        defaultWarehouseDeliveryInfo,
        warehouseSelectionBlocked,
        warehouses: warehousesList,
        isProductBlocked,
        stocks,
        showStockSummary,
        isShowroomWarehouseEnabled,
        warehouseGroups,
        calculateSummaryStock,
      }
    },
  })
